import React from 'react';

export interface SchemaMarkupProps {
  schema: object;
}

export const SchemaMarkup: React.FC<SchemaMarkupProps> = ({ schema }) => {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schema, null, 2)
      }}
    />
  );
};

// Schema factory functions for different schema types
export const createOrganizationSchema = (
  name: string,
  url: string,
  logo: string,
  description: string,
  address?: {
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  }
) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name,
  url,
  logo,
  description,
  ...(address && {
    address: {
      "@type": "PostalAddress",
      ...address
    }
  })
});

export const createLocalBusinessSchema = (
  name: string,
  description: string,
  address: {
    streetAddress?: string;
    addressLocality: string;
    addressRegion: string;
    postalCode?: string;
    addressCountry?: string;
  },
  geo: {
    latitude: number;
    longitude: number;
  },
  url?: string,
  telephone?: string,
  openingHours?: string[],
  priceRange?: string
) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name,
  description,
  address: {
    "@type": "PostalAddress",
    ...address
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: geo.latitude,
    longitude: geo.longitude
  },
  ...(url && { url }),
  ...(telephone && { telephone }),
  ...(openingHours && { openingHours }),
  ...(priceRange && { priceRange })
});

export const createEventSchema = (
  name: string,
  description: string,
  startDate: string,
  endDate: string,
  location: {
    name: string;
    address: {
      streetAddress?: string;
      addressLocality: string;
      addressRegion: string;
      postalCode?: string;
      addressCountry?: string;
    };
    geo: {
      latitude: number;
      longitude: number;
    };
  },
  organizer?: {
    name: string;
    url?: string;
  },
  url?: string,
  image?: string,
  offers?: {
    price: string;
    priceCurrency: string;
    availability: string;
    url?: string;
  }
) => ({
  "@context": "https://schema.org",
  "@type": "Event",
  name,
  description,
  startDate,
  endDate,
  eventStatus: "https://schema.org/EventScheduled",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  location: {
    "@type": "Place",
    name: location.name,
    address: {
      "@type": "PostalAddress",
      ...location.address
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: location.geo.latitude,
      longitude: location.geo.longitude
    }
  },
  ...(organizer && {
    organizer: {
      "@type": "Person",
      ...organizer
    }
  }),
  ...(url && { url }),
  ...(image && { image }),
  ...(offers && {
    offers: {
      "@type": "Offer",
      ...offers
    }
  })
});

export const createBreadcrumbSchema = (
  breadcrumbs: Array<{
    name: string;
    url: string;
  }>
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: breadcrumbs.map((crumb, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: crumb.name,
    item: crumb.url
  }))
});

export const createSearchActionSchema = (
  targetUrl: string,
  queryInput: string = "search_term_string"
) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  url: window.location.origin,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${targetUrl}?q={${queryInput}}`
    },
    "query-input": `required name=${queryInput}`
  }
});

export const createProductSchema = (
  name: string,
  description: string,
  image: string,
  offers: {
    price: string;
    priceCurrency: string;
    availability: string;
    url?: string;
  },
  brand?: string,
  category?: string,
  aggregateRating?: {
    ratingValue: number;
    reviewCount: number;
  }
) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name,
  description,
  image,
  offers: {
    "@type": "Offer",
    ...offers
  },
  ...(brand && { brand }),
  ...(category && { category }),
  ...(aggregateRating && {
    aggregateRating: {
      "@type": "AggregateRating",
      ...aggregateRating
    }
  })
});

export const createCreativeWorkSchema = (
  name: string,
  description: string,
  creator: {
    name: string;
    url?: string;
  },
  dateCreated: string,
  genre?: string,
  keywords?: string[],
  url?: string,
  image?: string,
  license?: string
) => ({
  "@context": "https://schema.org",
  "@type": "CreativeWork",
  name,
  description,
  creator: {
    "@type": "Person",
    ...creator
  },
  dateCreated,
  ...(genre && { genre }),
  ...(keywords && { keywords }),
  ...(url && { url }),
  ...(image && { image }),
  ...(license && { license })
});

export default SchemaMarkup; 